import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Plus, Trash2, Brain } from 'lucide-react';
import {
  useSessionInterpretations,
  useAddInterpretation,
  useRemoveInterpretation,
} from '@/hooks/useOptiBayInterpretation';
import type { OptiBaySession, OptiBayConfidence } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayInterpretationStepProps {
  session: OptiBaySession;
  onNext: () => void;
  onBack: () => void;
}

const CONFIDENCE_CONFIG: Record<OptiBayConfidence, { label: string; color: string }> = {
  low: { label: 'Low', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  high: { label: 'High', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
};

const ROOT_CAUSE_CATEGORIES = [
  'Electrical',
  'Mechanical',
  'Fluid/Hydraulic',
  'Software/Module',
  'Sensor/Input',
  'Wiring/Connection',
  'Component Wear',
  'External Damage',
  'Other',
];

export function OptiBayInterpretationStep({ session, onNext, onBack }: OptiBayInterpretationStepProps) {
  const { data: interpretations = [] } = useSessionInterpretations(session.id);
  const addInterpretation = useAddInterpretation();
  const removeInterpretation = useRemoveInterpretation();

  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState('');
  const [confidence, setConfidence] = useState<OptiBayConfidence>('medium');
  const [rootCause, setRootCause] = useState('');

  const handleAdd = async () => {
    if (!text.trim()) return;
    await addInterpretation.mutateAsync({
      sessionId: session.id,
      interpretationText: text.trim(),
      confidence,
      rootCauseCategory: rootCause || undefined,
    });
    setText('');
    setConfidence('medium');
    setRootCause('');
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Interpretation</h4>
        <Badge variant="secondary" className="text-xs">
          {interpretations.length} finding{interpretations.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 p-3 text-xs text-blue-800 dark:text-blue-200">
        <Brain className="h-3.5 w-3.5 inline mr-1.5" />
        Based on your evidence and verification tests, document what the data tells you. Focus on patterns — not repair recommendations.
      </div>

      {interpretations.length === 0 && !showForm ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          No interpretations yet. Document your diagnostic conclusions.
        </p>
      ) : (
        <div className="space-y-2">
          {interpretations.map(interp => {
            const conf = CONFIDENCE_CONFIG[interp.confidence as OptiBayConfidence] || CONFIDENCE_CONFIG.medium;
            return (
              <div key={interp.id} className="border rounded-lg p-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Brain className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', conf.color)}>
                      {conf.label} confidence
                    </span>
                    {interp.root_cause_category && (
                      <Badge variant="outline" className="text-[10px]">{interp.root_cause_category}</Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-destructive/70 hover:text-destructive"
                    onClick={() => removeInterpretation.mutate({ id: interp.id, sessionId: session.id })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <p className="text-xs pl-5">{interp.interpretation_text}</p>
              </div>
            );
          })}
        </div>
      )}

      {showForm ? (
        <div className="border rounded-lg p-3 space-y-3">
          <Textarea
            placeholder="What does the evidence tell you? Describe the pattern or finding…"
            value={text}
            onChange={e => setText(e.target.value)}
            className="text-xs min-h-[70px]"
          />
          <div className="flex gap-1.5">
            {(Object.keys(CONFIDENCE_CONFIG) as OptiBayConfidence[]).map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setConfidence(c)}
                className={cn(
                  'text-[10px] px-2 py-1 rounded-full border font-medium transition-colors',
                  confidence === c
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'hover:bg-accent/50 text-muted-foreground',
                )}
              >
                {CONFIDENCE_CONFIG[c].label}
              </button>
            ))}
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground mb-1 block">Root Cause Category (optional)</label>
            <div className="flex gap-1 flex-wrap">
              {ROOT_CAUSE_CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setRootCause(rootCause === cat ? '' : cat)}
                  className={cn(
                    'text-[10px] px-2 py-0.5 rounded-full border transition-colors',
                    rootCause === cat
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'hover:bg-accent/50 text-muted-foreground',
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAdd} disabled={!text.trim() || addInterpretation.isPending}>
              Add Interpretation
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Interpretation
        </Button>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button size="sm" onClick={onNext} disabled={interpretations.length === 0}>
          Continue to Repair
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
